"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { motion, useReducedMotion } from "framer-motion"
import { ChevronLeft } from "lucide-react"
import { useI18n } from "@/i18n"

interface OnboardingProgressProps {
  current: number
  total: number
  /** Back chevron sits IN the progress header (owner reference video 2026-08-29): one compact wayfinding row, not a counter + a separate footer button. */
  onBack?: () => void
  canGoBack: boolean
}

/**
 * Slim animated progress pill (owner reference: Rootd-style onboarding).
 * No "Step 2 of 8" text on screen — the filling bar IS the progress story;
 * the exact position stays available to screen readers. The fill springs
 * to its new width on every answer, which is the little dopamine beat that
 * makes an 8-screen flow feel like it's moving.
 */
export function OnboardingProgress({ current, total, onBack, canGoBack }: OnboardingProgressProps) {
  const { t } = useI18n()
  const reduce = useReducedMotion()
  const fraction = Math.min(1, Math.max(0.06, current / total))

  return (
    <div className="mx-auto flex w-full max-w-2xl items-center gap-3">
      {canGoBack && onBack ? (
        <button
          type="button"
          aria-label={t("onboarding.shell.back")}
          onClick={onBack}
          className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-paper text-ink shadow-sm transition-transform hover:border-green/60 active:scale-90"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>
      ) : (
        <span aria-hidden="true" className="size-9 shrink-0" />
      )}
      <div
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={current}
        aria-label={t("onboarding.shell.stepOf", { current, total })}
        className="h-3.5 flex-1 overflow-hidden rounded-bb-full border border-line bg-paper shadow-inner"
      >
        <motion.div
          className="h-full rounded-bb-full bg-gradient-to-r from-green to-lime"
          initial={false}
          animate={{ width: `${fraction * 100}%` }}
          transition={
            reduce ? { duration: 0 } : { type: "spring", stiffness: 220, damping: 28 }
          }
        />
      </div>
      <span className="sr-only" aria-live="polite">
        {t("onboarding.shell.stepOf", { current, total })}
      </span>
    </div>
  )
}
